<template>
  <v-container fluid>
    <TitleBar title="Update Employee" />
    <v-form class="employee-form mt-5" @submit.prevent="submit">
      <v-alert v-if="loadError" type="error" text role="alert">
        {{ loadError }} <v-btn text small color="error" @click="loademployee">Retry</v-btn>
      </v-alert>
      <v-skeleton-loader v-if="!employeeLoaded && !loadError" type="article, article" class="rounded-xl mb-5" />
      <EmployeeFormFields v-if="employeeLoaded" :employee="employee" :states="states" :countries="countries" :errors="formErrors"
        :show-driver="Boolean(locationModules && locationModules.terminalordering)"
        :show-technician="Boolean(locationModules && (locationModules.terminalrepairs || locationModules.terminalautoshop))"
        @touch="touchField" />

      <v-card v-if="employeeLoaded" outlined class="rounded-xl employee-form-section mb-5">
        <v-card-title class="employee-form-section__title">
          <v-icon color="primary" class="mr-3">mdi-store-outline</v-icon>
          <div><div>Locations &amp; roles</div><div class="employee-form-section__subtitle">Manage access and responsibilities for each location</div></div>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-alert v-if="optionsError" type="error" text role="alert">
            {{ optionsError }} <v-btn text small color="error" @click="loadAssignmentOptions">Retry</v-btn>
          </v-alert>
          <v-row align="start">
            <v-col cols="12" md="5">
              <v-autocomplete v-model="selectedLocation" outlined label="Location" :items="locations" item-text="name" return-object :disabled="Boolean(optionsError)" />
            </v-col>
            <v-col cols="12" md="5">
              <v-autocomplete v-model="selectedRole" outlined label="Role at this location" :items="roles" item-text="name" return-object :disabled="Boolean(optionsError)" />
            </v-col>
            <v-col cols="12" md="2">
              <v-btn color="primary" block min-height="48" :disabled="!selectedLocation || !selectedRole" @click="addLocation">Add assignment</v-btn>
            </v-col>
          </v-row>
          <div v-if="activeAssignments.length" class="assignment-list" role="list" aria-label="Assigned locations and roles">
            <div v-for="item in activeAssignments" :key="assignmentKey(item)" class="assignment-row" role="listitem">
              <v-icon color="primary" class="mr-3">mdi-store-outline</v-icon>
              <div class="assignment-row__content"><strong>{{ item.Location && item.Location.name }}</strong><span>{{ item.EmployeeRole ? item.EmployeeRole.name : 'No role' }}</span></div>
              <v-btn icon :aria-label="`Remove ${item.Location && item.Location.name} assignment`" @click="deleteLocation(employee.ActiveLocations.indexOf(item))"><v-icon>mdi-delete-outline</v-icon></v-btn>
            </div>
          </div>
          <v-alert v-else text color="primary" icon="mdi-information-outline">No locations assigned yet.</v-alert>
        </v-card-text>
      </v-card>

      <v-card v-if="employeeLoaded" outlined class="rounded-xl employee-form-section mb-5">
        <v-card-title class="employee-form-section__title">
          <v-icon color="primary" class="mr-3">mdi-eye-off-outline</v-icon>
          <div><div>Payee visibility</div><div class="employee-form-section__subtitle">Optional controls for the employee's payee list</div></div>
        </v-card-title>
        <v-divider />
        <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="9" lg="10">
            <span class="font-weight-medium">Hide Payee</span>
            <p class="font-weight-light mb-0">
              Hide selected payees from {{ employee.firstName }}
              {{ employee.lastName }}.
            </p>
          </v-col>

          <v-col cols="12" md="3" lg="2">
            <v-switch
              :loading="payeeLoading"
              v-model="hidePayee"
              color="primary"
              :label="hidePayee ? 'Enabled' : 'Disabled'"
            />
          </v-col>
        </v-row>

        <v-card class="mt-3" flat outlined v-if="hidePayee">
          <v-card-text>
            <v-row class="mb-3" align="center">
              <v-col cols="12" sm="6">
                <span class="font-weight-medium">Payee List</span>
              </v-col>

              <v-spacer />

              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="search"
                  label="Search"
                  :append-icon="search ? 'close' : ''"
                  @click:append="setNull()"
                  single-line
                  outlined
                  hide-details
                  class="rounded"
                />
              </v-col>
            </v-row>

            <v-data-table
              v-model="selected"
              :headers="headers"
              :items="Payees"
              :search="search"
              :options.sync="tableOptions"
              item-key="id"
              show-select
              class="elevation-0 table-header"
            >
              <template v-slot:item.accountNumber="{ item }">
                <span>{{
                  item.accountNumber ? item.accountNumber : "N/A"
                }}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
        </v-card-text>
      </v-card>

      <div class="employee-form-actions">
        <v-btn text min-height="44" :disabled="saving" @click="cancel">Cancel</v-btn>
        <v-btn color="primary" depressed min-height="44" type="submit" :loading="saving" :disabled="!employeeLoaded">Save changes</v-btn>
      </div>
    </v-form>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import payeeService from "@/modules/Payee/service.js";
import employeeService from "../service";
import locationService from "../../Location/service.js";
import rolesService from "../modules/Roles/service.js";
import EmployeeFormFields from "./EmployeeFormFields.vue";

const { validationMixin } = require("vuelidate");
const {
  required,
  minLength,
  email,
  requiredIf,
} = require("vuelidate/lib/validators");

export default {
  name: "employee-edit",
  components: { EmployeeFormFields },
  props: {
    isModal: Boolean,
  },
  data() {
    return {
      search: "",
      tableOptions: {
        sortBy: ["name"],
      },
      selected: [],
      headers: [
        {
          text: "Name",
          align: "left",
          value: "name",
        },
        {
          text: "Phone Number",
          align: "left",
          value: "phoneNumber",
        },
        {
          text: "Email",
          align: "left",
          value: "email",
        },
        {
          text: "Account",
          align: "left",
          value: "accountNumber",
        },
      ],
      payeeLoading: false,
      saving: false,
      loadError: "",
      optionsError: "",
      employeeLoaded: false,
      Payees: [],
      hidePayee: false,
      locationId: null,
      showPassword: false,
      locations: [],
      roles: [],
      selectedLocation: null,
      selectedRole: null,
      locationTable: {
        headers: [
          {
            text: "Location",
            value: "Location",
            sortable: false,
          },
          {
            text: "Role",
            value: "EmployeeRole",
            sortable: false,
          },
          {
            text: "Action",
            value: "action",
            sortable: false,
            align: "center",
          },
        ],
      },
      employee: {
        id: null,
        firstName: null,
        lastName: null,
        phoneNumber: null,
        accountNumber: null,
        pinCode: null,
        confirmPin: null,
        hourlyRate: null,
        email: null,
        website: null,
        note: null,
        Address: {},
        ActiveLocations: [],
        metadata: {
          isDriver: false,
          isTechnician: false,
          employeeHours: {
            breakHours: null,
            workingHours: null,
          },
          hidePayee: {
            list: [],
            isEnable: false,
          },
          timeClockLogin: {
            admin: false,
            employee: false,
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters("global", [
      "states",
      "countries",
      "defaultCountryCode",
      "location",
    ]),
    ...mapGetters("integration", ["locationModules", "modules"]),
    checkTimeClockModule() {
      if (
        this.locationModules &&
        this.locationModules.terminaltimeclock &&
        this.locationModules.terminaltimeclock.LocationIntegrations &&
        this.locationModules.terminaltimeclock.LocationIntegrations.length
      ) {
        return true;
      }
      return false;
    },
    Address() {
      return this.employee.Address || {};
    },
    activeAssignments() {
      return this.employee.ActiveLocations.filter((item) => !item.delete);
    },
    formErrors() {
      return {
        firstName: this.firstNameErrors,
        lastName: this.lastNameErrors,
        phoneNumber: this.phoneNumberErrors,
        email: this.emailErrors,
        pinCode: this.pinErrors,
        addressLine1: this.addressLine1Errors,
        city: this.cityErrors,
      };
    },
    firstNameErrors() {
      const errors = [];
      if (!this.$v.employee.firstName.$dirty) return errors;
      if (!this.$v.employee.firstName.required) {
        errors.push("This field is required.");
      }
      if (!this.$v.employee.firstName.minLength) {
        errors.push("First name must be at least 2 characters long.");
      }
      return errors;
    },
    lastNameErrors() {
      const errors = [];
      if (!this.$v.employee.lastName.$dirty) return errors;
      if (!this.$v.employee.lastName.minLength) {
        errors.push("Last name must be at least 2 characters long.");
      }
      return errors;
    },
    phoneNumberErrors() {
      const errors = [];
      if (!this.$v.employee.phoneNumber.$dirty) return errors;
      if (!this.$v.employee.phoneNumber.minLength) {
        errors.push("Phone number must be at least 10 digits.");
      }
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.employee.email.$dirty) return errors;
      if (!this.$v.employee.email.email) {
        errors.push("Email address is not valid.");
      }
      return errors;
    },
    addressLine1Errors() {
      const errors = [];
      if (!this.$v.employee.Address.addressLine1.$dirty) return errors;
      if (!this.$v.employee.Address.addressLine1.minLength) {
        errors.push("Address must be at least 2 characters.");
      }
      if (!this.$v.employee.Address.addressLine1.required) {
        errors.push("This field is required.");
      }
      return errors;
    },
    cityErrors() {
      const errors = [];
      if (!this.$v.employee.Address.city.$dirty) return errors;
      if (!this.$v.employee.Address.city.required) {
        errors.push("City is required.");
      }
      return errors;
    },
    pinErrors() {
      const errors = [];
      if (!this.$v.employee.pinCode.$dirty) return errors;
      if (!this.$v.employee.pinCode.required) {
        errors.push("This field is required.");
      }
      if (!this.$v.employee.pinCode.minLength) {
        errors.push("Credential must be at least 4 characters.");
      }
      return errors;
    },
  },
  watch: {
    hidePayee(val) {
      this.employee.metadata.hidePayee.isEnable = val;
      if (val) {
        this.getPayee();
      }
    },
    Address() {
      if (this.employee.Address && Object.keys(this.employee.Address).length) {
        if (
          !this.employee.Address.city &&
          this.location &&
          this.location.Address
        ) {
          this.employee.Address.city = this.location.Address.city;
        }
        if (
          !this.employee.Address.StateId &&
          this.location &&
          this.location.Address
        ) {
          this.employee.Address.StateId = this.location.Address.StateId;
        }
        if (
          !this.employee.Address.CountryCode &&
          this.location &&
          this.location.Address
        ) {
          this.employee.Address.CountryCode = this.location.Address.CountryCode;
        }
      }
    },
  },
  created() {
    if (this.$route.params && this.$route.params.LocationId) this.locationId = this.$route.params.LocationId;
    this.loadAssignmentOptions();
    this.loademployee();
  },
  methods: {
    async loadAssignmentOptions() {
      this.optionsError = "";
      try {
        this.roles = (await rolesService.getAll()).data;
        this.locations = (await locationService.getAll({ limit: 40 })).data;
      } catch (error) {
        this.optionsError = "Locations and roles could not be loaded. Existing assignments are unchanged.";
      }
    },
    assignmentKey(item) {
      return item.id || (item.Location && item.Location.id);
    },
    touchField(field) {
      if (field === "addressLine1" || field === "city") this.$v.employee.Address[field].$touch();
      else if (this.$v.employee[field]) this.$v.employee[field].$touch();
    },
    setNull() {
      this.search = "";
    },
    getPayee() {
      this.payeeLoading = true;
      return payeeService
        .getAll({ limit: 500 })
        .then((response) => {
          this.Payees = response.data;
          this.payeeLoading = false;
          if (
            this.employee.metadata.hidePayee &&
            this.employee.metadata.hidePayee.isEnable &&
            this.employee.metadata.hidePayee.list &&
            this.employee.metadata.hidePayee.list.length > 0
          ) {
            this.employee.metadata.hidePayee.list.map((row) => {
              const found = this.Payees.find((payee) => payee.id === row);
              if (found) {
                this.selected.push(found);
              }
              return true;
            });
          }
        })
        .catch((error) => {
          console.log("error", error);
          this.payeeLoading = false;
          this.$swal("Payee", "Can't get payee list", "error");
        });
    },
    loademployee() {
      this.loadError = "";
      this.employeeLoaded = false;
      if (
        !this.$route.params ||
        (this.$route.params &&
          this.$route.params.Employee &&
          !this.$route.params.Employee.id)
      ) {
        return this.$router.go(-1);
      }
      const id = this.$route.params.Employee.id;
      this.$Progress.start();
      return employeeService
        .getById(id, { includeLocations: true })
        .then((response) => {
          this.$Progress.finish();
          this.employee.id = response.data.id;
          this.employee.firstName = response.data.firstName;
          this.employee.lastName = response.data.lastName;
          this.employee.phoneNumber = response.data.phoneNumber;
          this.employee.accountNumber = response.data.accountNumber;
          this.employee.email = response.data.email;
          this.employee.website = response.data.website;
          this.employee.note = response.data.note;
          this.employee.hourlyRate = response.data.hourlyRate;
          this.employee.pinCode = response.data.pinCode;

          if (response.data.metadata) {
            this.employee.metadata = response.data.metadata;

            if (!this.employee.metadata.employeeHours) {
              this.employee.metadata.employeeHours = {
                breakHours: null,
                workingHours: null,
              };
            }
            if (!this.employee.metadata.timeClockLogin) {
              this.employee.metadata.timeClockLogin = {
                admin: false,
                employee: false,
              };
            }
            if (!this.employee.metadata.hidePayee) {
              this.employee.metadata.hidePayee = {
                list: [],
                isEnable: false,
              };
            } else {
              this.hidePayee = this.employee.metadata.hidePayee.isEnable;
            }
          }

          this.employee.ActiveLocations = [];
          Promise.all(
            response.data.ActiveLocations.map(async (location) => {
              this.selectedLocation = location;
              await this.addLocation();
              return true;
            })
          );

          this.employee.Address = response.data.Address || {};
          this.employeeLoaded = true;
        })
        .catch((error) => {
          this.$Progress.fail();
          this.loadError = "Employee details could not be loaded. Please go back and try again.";
        });
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            timer: 2000,
          })
          .fire({
            type: "error",
            title: "Please fix the highlighted fields.",
          });
      } else {
        this.saving = true;
        this.$Progress.start();

        this.employee.metadata.hidePayee.list = [];
        if (this.selected && this.selected.length > 0) {
          this.selected.map((row) => {
            this.employee.metadata.hidePayee.list.push(row.id);
            return true;
          });
        }

        employeeService
          .update(this.employee.id, this.employee)
          .then(() => {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                timer: 2000,
              })
              .fire({
                type: "success",
                title: "Employee updated successfully.",
              });
            this.$Progress.finish();
            this.saving = false;
            this.close();
          })
          .catch((err) => {
            console.log(err);
            this.$swal
              .mixin({
                toast: true,
                showConfirmButton: true,
                position: "top-end",
                timer: 3000,
              })
              .fire({
                type: "error",
                title: err.data ? err.data.message : err.message,
              });
            this.$Progress.fail();
            this.saving = false;
          });
      }
    },
    addLocation() {
      if (!this.selectedLocation) {
        return;
      }

      if (!this.selectedLocation.Location) {
        this.selectedLocation = {
          Location: this.selectedLocation,
          EmployeeRole: this.selectedRole,
        };
      }

      if (this.selectedLocation.delete) {
        this.selectedLocation.delete = false;
      } else {
        this.employee.ActiveLocations = this.employee.ActiveLocations.concat(
          this.selectedLocation
        );
      }

      const self = this;
      const foundIndex = this.locations.findIndex(function (element) {
        if (!self.selectedLocation.Location) {
          return self.selectedLocation.id === element.id;
        }
        return self.selectedLocation.Location.id === element.id;
      });

      if (foundIndex > -1) {
        this.locations.splice(foundIndex, 1);
        this.locations = [].concat(this.locations);
      }

      this.selectedLocation = 0;
      this.selectedRole = 0;
    },
    deleteLocation(index) {
      this.locations = [
        this.employee.ActiveLocations[index].Location,
        ...this.locations,
      ];
      this.employee.ActiveLocations[index].delete = true;
      this.employee.ActiveLocations = [].concat(this.employee.ActiveLocations);
    },
    cancel() {
      this.close();
    },
    close() {
      if (!this.isModal) {
        this.$router.go(-1);
      } else {
        this.$emit("close");
      }
    },
  },
  mixins: [validationMixin],
  validations: {
    employee: {
      pinCode: {
        required,
        minLength: minLength(4),
      },
      firstName: {
        required,
        minLength: minLength(2),
      },
      lastName: {
        minLength: minLength(1),
      },
      phoneNumber: {
        minLength: minLength(10),
      },
      email: {
        email,
      },
      Address: {
        addressLine1: {
          required: requiredIf(function (nestedModel) {
            return Object.keys(nestedModel).length > 0;
          }),
          minLength: minLength(2),
        },
        city: {
          required: requiredIf(function (nestedModel) {
            return Object.keys(nestedModel).length > 0;
          }),
        },
      },
    },
  },
};
</script>

<style scoped>
.employee-form { max-width: 1180px; margin: 0 auto; padding-bottom: 88px; }
.employee-form-section { background: var(--app-surface, #fff); }
.employee-form-section__title { font-size: 1.05rem; line-height: 1.35; padding: 18px 20px; }
.employee-form-section__subtitle { color: var(--app-text-muted, rgba(0, 0, 0, 0.6)); font-size: .8125rem; font-weight: 400; margin-top: 2px; }
.assignment-list { border: 1px solid var(--app-border, rgba(0,0,0,.12)); border-radius: 12px; overflow: hidden; }
.assignment-row { align-items: center; display: flex; min-height: 58px; padding: 8px 12px; }
.assignment-row + .assignment-row { border-top: 1px solid var(--app-border, rgba(0,0,0,.12)); }
.assignment-row__content { display: flex; flex: 1; flex-direction: column; min-width: 0; }
.assignment-row__content span { color: var(--app-text-muted, rgba(0,0,0,.6)); font-size: .8125rem; }
.employee-form-actions { align-items: center; background: var(--app-surface, #fff); border-top: 1px solid var(--app-border, rgba(0,0,0,.12)); bottom: 0; display: flex; gap: 8px; justify-content: flex-end; margin: 0 -12px; padding: 12px 20px; position: sticky; z-index: 4; }
@media (max-width: 599px) { .employee-form-actions .v-btn { flex: 1; } }
</style>
